const adminIdp = {
  actions: {
    backToClients: 'Back to clients',
    backToClientScopes: 'Back to client scopes',
    cancel: 'Cancel',
    closeAnyway: 'Close anyway',
    continueSaving: 'Keep saving',
    copy: 'Copy',
    create: 'Create',
    delete: 'Delete',
    done: 'I have saved it',
    edit: 'Edit',
    manageClientScopes: 'Manage client scopes',
    rotateKey: 'Rotate signing key',
    rotateSecret: 'Rotate',
    save: 'Save',
  },
  breadcrumbs: {
    label: 'Page hierarchy',
  },
  description:
    'Enable this service to let downstream systems sign users in through Hub. Copy the connection URLs here and manage clients on the Clients page.',
  errors: {
    rotateKey: 'Failed to rotate the signing key.',
    save: 'Failed to save the OIDC Provider status.',
  },
  loadFailed: 'Unable to load OIDC Identity Provider configuration.',
  parameters: {
    authorizationEndpoint: 'Authorization endpoint',
    discoveryUrl: 'Discovery URL',
    hint: 'These URLs are generated automatically. Copy them when configuring a downstream system.',
    introspectionEndpoint: 'Introspection endpoint',
    issuer: 'Issuer',
    jwksUri: 'JWKS URI',
    oauthDiscoveryUrl: 'OAuth authorization server metadata',
    revocationEndpoint: 'Revocation endpoint',
    title: 'Provider parameters',
    tokenEndpoint: 'Token endpoint',
    userinfoEndpoint: 'UserInfo endpoint',
  },
  provider: {
    hint: 'When disabled, Discovery and JWKS remain readable while authorize, token, UserInfo, and silent session preparation stop.',
    title: 'OIDC Provider',
    toggle: 'Enable OIDC Provider',
  },
  signingKey: {
    confirmTitle: 'Rotate signing key',
    hint: 'Use only for a suspected compromise or planned rotation. Retired public keys remain available for unexpired ID Tokens.',
    rotateConfirm:
      'Rotate the signing key now? New ID Tokens will immediately use the new key and downstream systems must refresh JWKS.',
    title: 'Signing key',
  },
  status: { disabled: 'Disabled', enabled: 'Enabled' },
  success: {
    disabled: 'OIDC Provider disabled',
    enabled: 'OIDC Provider enabled',
    keyRotated: 'Signing key rotated: {{kid}}',
  },
  title: 'OIDC Identity Provider',
  setupFlow: {
    label: 'Identity Provider configuration order',
    steps: {
      provider: { title: 'Enable provider', description: 'Confirm issuer and protocol endpoints' },
      scopes: { title: 'Define scopes', description: 'Configure permissions and identity fields' },
      resources: {
        title: 'Register MCP resources',
        description: 'Bind MCP Servers and allowed scopes',
      },
      clients: {
        title: 'Connect clients',
        description: 'Issue credentials and configure callbacks',
      },
    },
  },
  clients: {
    applicationBoundElsewhere: 'Bound to another client',
    applicationsDescription:
      'One client can bind multiple Hub Embed URL apps. Each app currently selects at most one client.',
    clientScopesDescription:
      'Select the field groups assigned to this client. Default scopes are automatic; optional scopes are returned only when explicitly requested.',
    confirmDelete: 'Delete client “{{name}}”? Bound apps will be unbound.',
    confirmDeleteTitle: 'Delete client',
    create: 'Create client',
    description:
      'Each client represents a trust relationship with a system connecting to Hub OIDC. Create and deliver credentials first, then bind Hub apps as needed.',
    detailTitle: 'OIDC client',
    editTitle: 'Edit / {{name}}',
    empty: 'No OIDC clients yet.',
    noApplications: 'No Embed URL apps yet.',
    search: 'Search client name, client ID, or app',
    title: 'Clients',
    clientKinds: {
      mcpOauth: 'MCP OAuth',
      oidcEmbed: 'OpenID Connect',
    },
    columns: {
      actions: 'Actions',
      applications: 'Apps',
      clientId: 'Client ID',
      clientKind: 'Client type',
      name: 'Name',
      protocol: 'Protocol',
      scopes: 'Scopes',
      status: 'Status',
    },
    credentials: {
      closeConfirm: 'The one-time integration package has not been copied. Close anyway?',
      closeConfirmTitle: 'Client credentials are not saved',
      copyFailed: 'Copy failed. Check clipboard permission and try again.',
      copyPackage: 'Copy integration package',
      copySuccess: 'Copied to clipboard.',
      notConfigured: 'Not configured',
      oneTimeHint:
        'The client secret is shown only once. Save it securely and deliver it to the downstream system now.',
      protocol: 'Protocol requirements',
      providerLoadFailed:
        'Provider parameters failed to load. The client secret is still available.',
      providerLoading: 'Loading provider parameters…',
      retryProvider: 'Retry',
      rotate: 'Rotate client secret',
      rotateConfirm: 'Rotate the client secret? The old secret stops working immediately.',
      rotateConfirmTitle: 'Rotate client secret',
      rotateHint:
        'After rotation, update the downstream system with the new secret or existing sign-in will fail.',
      rotateTitle: 'Client secret',
      title: 'Save client credentials',
    },
    errors: {
      create: 'Failed to create the client.',
      delete: 'Failed to delete the client.',
      load: 'Failed to load clients.',
      loadDetail: 'Failed to load the client.',
      noActiveResources: 'Create and enable an OAuth resource first.',
      noEnabledScopes: 'Create and enable a client scope first.',
      rotateSecret: 'Failed to rotate the client secret.',
      update: 'Failed to save the client.',
    },
    filters: {
      allClientKinds: 'All client types',
      clientKind: 'Client type',
    },
    fields: {
      assignScope: 'Assign {{name}}',
      allowedScopes: 'Allowed client scopes',
      allowedScopesHint:
        'Dynamic MCP clients can request only the scopes selected for this resource.',
      authMethod: 'Token endpoint authentication method',
      authMethodHelp:
        'How the downstream system sends its client secret when exchanging a code for tokens. The default works for most systems.',
      callbackUrls: 'Callback URL',
      callbackUrlsHelp:
        'Where the downstream system receives the sign-in result. You can leave it empty now; add the HTTPS URL before integration testing.',
      clientKind: 'Client type',
      clientKindHelp:
        'Use OpenID Connect for Embed apps and MCP OAuth for external systems accessing an MCP Server.',
      defaultScopes: 'Default client scopes',
      clientId: 'Client ID',
      clientSecret: 'Client secret',
      resource: 'OAuth resource',
      resourceHint:
        'Access tokens issued to this client can target only the selected Resource URI.',
      name: 'Client name',
      nameHelp: 'An admin-facing name that distinguishes the downstream system or environment.',
      scope: 'Scope',
      scopeFallbackDescription: 'No description is available for this scope.',
      scopeAssignment: 'Assignment for {{name}}',
      status: 'Status',
      statusHelp: 'Disabling the client blocks sign-in but keeps its settings and app bindings.',
    },
    formHints: {
      mcp: 'External MCP clients use Authorization Code with PKCE to obtain an access token bound to one resource.',
      oidc: 'Hub Embed apps use OpenID Connect sign-in and receive identity claims through client scopes.',
    },
    placeholders: {
      callbackUrls: 'https://client.example.com/oidc/callback',
      name: 'For example, Production web client',
    },
    rowActions: {
      edit: 'Edit details',
      open: 'Open actions for {{name}}',
    },
    scopeAssignments: {
      default: 'Default',
      optional: 'Optional',
      unassigned: 'Unassigned',
    },
    success: {
      created: 'Client created.',
      deleted: 'Client deleted.',
      secretRotated: 'Client secret rotated.',
      updated: 'Client saved.',
    },
    tabs: {
      applications: 'Applications',
      clientScopes: 'Client scopes',
      credentials: 'Credentials',
      label: 'Client detail tabs',
      settings: 'Settings',
    },
  },
  resources: {
    confirmDelete:
      'Delete OAuth resource “{{name}}”? The system rejects deletion while a client still uses it.',
    confirmDeleteTitle: 'Delete OAuth resource',
    confirmRotate:
      'Rotate the introspection secret for “{{name}}”? The old secret stops working immediately.',
    confirmRotateTitle: 'Rotate introspection secret',
    create: 'Create OAuth resource',
    description:
      'Each resource represents an MCP Server protected by Hub and owns an independent introspection credential.',
    formHint:
      'A resource is the sole audience of an access token. Deliver its one-time introspection credential securely to that MCP Server.',
    insecureHttpBadge: 'Insecure HTTP',
    insecureHttpWarning:
      'HTTP sends bearer tokens without transport encryption. Use it only for an explicitly enabled, isolated development or UAT private network, and migrate this resource to HTTPS before production.',
    editTitle: 'Edit / {{name}}',
    empty: 'No OAuth resources yet.',
    search: 'Search name, resource URI, or introspection client ID',
    title: 'OAuth resources',
    columns: {
      actions: 'Actions',
      introspectionClientId: 'Introspection client ID',
      name: 'Name',
      resourceUri: 'Resource URI',
      status: 'Status',
    },
    credentials: {
      closeConfirm:
        'The one-time Resource Server integration package has not been copied. Close anyway?',
      closeConfirmTitle: 'Resource Server credentials are not saved',
      copyFailed: 'Copy failed. Check clipboard permission and try again.',
      copyPackage: 'Copy Resource Server package',
      oneTimeHint:
        'The introspection secret is shown only once. Save it securely and deliver it to the MCP Server now.',
      providerLoadFailed:
        'Provider parameters failed to load. The introspection secret is still available.',
      providerLoading: 'Loading provider parameters…',
      retryProvider: 'Retry',
      title: 'Save Resource Server credentials',
    },
    errors: {
      create: 'Failed to create the OAuth resource.',
      delete: 'Failed to delete the OAuth resource. Make sure no client uses it.',
      load: 'Failed to load OAuth resources.',
      rotateSecret: 'Failed to rotate the introspection secret.',
      update: 'Failed to save the OAuth resource. A bound resource URI cannot be changed.',
    },
    fields: {
      allowedScopes: 'Allowed client scopes',
      allowedScopesHint:
        'Only selected scopes can be issued in access tokens targeting this resource.',
      introspectionClientId: 'Introspection client ID',
      introspectionSecret: 'Introspection secret',
      name: 'Resource name',
      nameHint:
        'Admin-facing only. Include the purpose and environment, such as “Knowledge MCP · UAT”.',
      noAvailableScopes: 'No scopes are available. Create and enable a client scope first.',
      resourceUri: 'Resource URI',
      resourceUriHint:
        'Use a stable HTTPS URI in production. It cannot change after binding a client.',
      scopeFallbackDescription: 'No description is available for this scope.',
      status: 'Status',
      statusHint:
        'Disabling blocks new authorization and makes existing tokens inactive during introspection.',
    },
    placeholders: {
      name: 'For example, Knowledge MCP Server',
      resourceUri: 'https://mcp.example.com',
    },
    rowActions: {
      open: 'Open actions for {{name}}',
      rotateSecret: 'Rotate secret',
    },
    success: {
      created: 'OAuth resource created.',
      deleted: 'OAuth resource deleted.',
      secretRotated: 'Introspection secret rotated.',
      updated: 'OAuth resource saved.',
    },
  },
  clientScopes: {
    builtIn: 'Built in',
    builtInReadOnly:
      'This client scope is maintained by the system and cannot be changed or deleted.',
    builtInReadOnlyTitle: 'System-managed configuration',
    confirmDelete: 'Delete client scope “{{name}}”?',
    confirmDeleteTitle: 'Delete client scope',
    create: 'Create client scope',
    createHint:
      'A client scope groups an OAuth permission with the identity fields released for it. Define the scope name first, then configure field mappings in its detail page.',
    custom: 'Custom',
    description:
      'Client scopes are reusable groups of fields. Configure protocol mappers in a scope, then assign the whole scope to an app as Default or Optional.',
    detailTitle: 'Client scope',
    empty: 'No client scopes yet.',
    search: 'Search client scopes',
    title: 'Client scopes',
    columns: {
      actions: 'Actions',
      description: 'Description',
      mappers: 'Mappers',
      name: 'Name',
      protocol: 'Protocol',
      status: 'Status',
      type: 'Type',
    },
    errors: {
      create: 'Failed to create the client scope.',
      delete: 'Failed to delete the client scope.',
      load: 'Failed to load client scopes.',
      loadDetail: 'Failed to load the client scope.',
      update: 'Failed to save the client scope.',
    },
    fields: {
      description: 'Description',
      descriptionHint:
        'Explain the granted capability so client and resource administrators can assign it safely.',
      displayName: 'Display name',
      displayNameHint: 'Admin-facing label. Hub attempts to generate the scope name from it.',
      enabled: 'Enabled',
      includeInTokenScope: 'Include in token scope',
      includeInTokenScopeHint:
        'When enabled, token responses include this client scope in the scope field.',
      name: 'Scope name',
      nameHint:
        'Machine-readable OAuth request value, such as documents.read. Keep it stable after creation.',
    },
    placeholders: {
      description:
        'For example, read documents available to the current user and release required identity fields.',
      name: 'For example, documents.read',
    },
    rowActions: {
      open: 'Open actions for {{name}}',
    },
    success: {
      created: 'Client scope created.',
      deleted: 'Client scope deleted.',
      updated: 'Client scope saved.',
    },
    tabs: {
      label: 'Client scope detail tabs',
      mappers: 'Mappers',
      settings: 'Settings',
    },
  },
  mappers: {
    claimSources: {
      email: 'Email address',
      external_person_id: 'Source system person ID',
      job_title: 'Job titles',
      name: 'Member name',
      organization_unit_codes: 'Organization codes',
      organization_unit_names: 'Organization names',
      person_types: 'Person types',
      phone_number: 'Phone number',
    },
    addRow: 'Add row',
    confirmDelete: 'Delete mapper “{{name}}”?',
    confirmDeleteTitle: 'Delete mapper',
    create: 'Add mapper',
    createTitle: 'Add field mapping',
    description:
      'Mappers control how identity data is written to ID Tokens, UserInfo, or introspection.',
    edit: 'Edit mapper',
    editTitle: 'Edit field mapping',
    empty: 'This client scope has no mappers.',
    emptyHint:
      'Add a field mapping before Hub can release the claim in a selected token or introspection response.',
    formDescription:
      'Selecting a source fills the name, claim name, and JSON type automatically. Override them only when needed.',
    inlineDescription: 'Add or change field mappings directly in the table, one row at a time.',
    readOnlyDescription: 'Mappings in built-in client scopes are system-managed and read-only.',
    rowActions: {
      open: 'Open actions for {{name}}',
    },
    sections: {
      behavior: 'Status and preview',
      behaviorDescription: 'Control whether this mapper is active and inspect its claim shape.',
      destinations: 'Destinations',
      destinationsDescription: 'Choose which protocol responses receive this field.',
      mapping: 'Field mapping',
      mappingDescription: 'Map one identity source or fixed value to a protocol claim.',
    },
    title: 'Protocol mappers',
    columns: {
      claimOutput: 'Claim output',
      destinations: 'Destinations',
      mappingSource: 'Mapping source',
      name: 'Name',
      source: 'Identity source',
      status: 'Status',
      tokenClaim: 'Token claim',
      type: 'Type',
      valueType: 'Value type',
    },
    errors: {
      create: 'Failed to create the mapper.',
      delete: 'Failed to delete the mapper.',
      update: 'Failed to save the mapper.',
    },
    fields: {
      addToIdToken: 'Add to ID Token',
      addToIntrospection: 'Add to introspection',
      addToUserinfo: 'Add to UserInfo',
      claimJsonType: 'JSON value type',
      enabled: 'Enabled',
      hardcodedValue: 'Hardcoded value',
      name: 'Mapper name',
      outputPreset: 'Destination preset',
      source: 'Identity source',
      tokenClaimName: 'Token claim name',
      type: 'Mapper type',
    },
    destinations: {
      idToken: 'Lets an OpenID Connect client identify the user in the sign-in result.',
      introspection:
        'Lets an MCP Resource Server read the claim while validating an opaque access token.',
      userinfo: 'Lets an OpenID Connect client query user identity with an access token.',
    },
    hints: {
      autoValueType:
        'Derived from the selected identity field schema so arrays are not emitted as strings.',
      claimJsonType: 'JSON type for the fixed value. Separate string-array items with commas.',
      enabled: 'Disabling keeps the mapping but stops writing this claim to every response.',
      hardcodedValue:
        'Every user granted this scope receives the same value. Never place a secret here.',
      name: 'Admin-facing description that does not appear in protocol responses.',
      outputPreset:
        'Choose a common use case first, then adjust the three destinations below if needed.',
      source: 'Only registered Hub identity fields approved for OIDC release can be selected.',
      tokenClaimName:
        'JSON response field, such as department. Do not use reserved names such as sub or scope.',
      type: 'Identity source reads the signed-in account. A hardcoded claim returns the same configured value for every user.',
    },
    origins: {
      account: 'Account field',
      account_attribute: 'Account attribute',
      organization: 'Organization relation',
      organization_attribute: 'Organization attribute',
    },
    outputPresets: {
      all: 'All destinations',
      custom: 'Custom',
      mcp: 'MCP · Introspection',
      oidc: 'OIDC · ID Token + UserInfo',
    },
    placeholders: {
      hardcodedValue: 'Enter a fixed value',
      name: 'For example, Department',
      source: 'Select an identity field',
      tokenClaimName: 'For example, department',
    },
    sourcePicker: {
      empty: 'No matching identity fields',
      searchLabel: 'Search identity fields',
      searchPlaceholder: 'Search field name or source path',
    },
    success: {
      created: 'Mapper created.',
      deleted: 'Mapper deleted.',
      updated: 'Mapper saved.',
    },
    preview: {
      description:
        'This previews only the current mapper claim, not the complete client scope payload, and contains no real user data.',
      title: 'Current field preview',
    },
    types: {
      hardcoded_claim: 'Hardcoded claim',
      hardcodedClaim: 'Hardcoded claim',
      identity_source: 'Identity source',
      identitySource: 'Identity source',
    },
    validation: {
      required: 'Complete the required fields and select at least one destination.',
    },
    valueTypes: {
      boolean: 'Boolean',
      number: 'Number',
      string: 'String',
      string_array: 'String array',
    },
  },
}

export default adminIdp
